/**
 * Single presentational SRP component: same markup and DOM structure the server renders and the
 * client hydrates in place (SRP islands rework Phase 2 — see LayoutDefault.tsx's SRP branch and
 * pages/index/srp.client.ts). Uses the same complex components as the full +Page (SearchHeader,
 * SearchSidebar, SearchResults, SearchPagination). Uses SearchContext hooks so sort, view mode,
 * pagination and hits are interactive (CustomHits, save/state buttons) on both server and client:
 * SSR renders with the same hook values the client will hydrate against, since SearchProvider
 * seeds them from the same initialViewModel (see SrpApp.tsx).
 *
 * There used to be a second, non-hydrated "static" render mode (a pre-generated-strings shell
 * rendered into a separate #srp-ssr sibling, swapped for this component after mount) plus a third,
 * "chromeFromParent: false" branch for a caller that always supplied its own chrome. Both were
 * dead: SrpApp.tsx's one live call site always resolves to this component with the interactive
 * SearchContext-backed rendering below, and nothing else called `SrpView` directly. Removed rather
 * than kept "just in case" — see commit history for the pre-removal implementation if the static
 * shell pattern is ever needed again.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useFormatMessage } from '../../i18n-review/client/Msg';
import { SearchHeader } from '../../pages/index/components/SearchHeader';
import { SearchSidebar } from '../../pages/index/components/SearchSidebar';
import { SearchResults } from '../../pages/index/components/SearchResults';
import { SearchPagination } from '../../pages/index/components/SearchPagination';
import { SrpAlertCta } from '../../components/job-alerts/SrpAlertCta';
import { MapViewModeToggle } from '../../components/map/MapViewModeToggle';
import { useViewMode } from '../../pages/index/hooks/useViewMode';
import { useSearchSort, useSearchQuery, useSearchFilters, useSearchResults, useSearchPage, useSearchLoading, useSearchError } from '../../contexts/SearchContext';
import { useLocationTrends } from '../../hooks/useLocationTrends';
import Footer from '../../components/Footer';
import { FeedbackModal } from '../../components/FeedbackModal';
import { KeyboardShortcutsTray } from '../../components/KeyboardShortcutsTray';
import type { SrpViewModel } from './SrpViewModel';
import { SrpTopEmployersStrip } from '../../components/SrpTopEmployersStrip';
import { SrpTopRolesStrip } from '../../components/SrpTopRolesStrip';
import { SrpActivitySankeyStripLive } from '../../components/srp/SrpActivitySankeyStrip';
import { getLocationFromFiltersAndRoute } from '../../pages/index/utils/seoUtils';
import { matchSearchIndexPath } from '../../pages/index/match-search-index-path';
import { useNavbarLiveIndexEnabled } from '../../utils/feature-flags/navbar-live-index-flag';
import { applySortPreference } from './apply-sort-preference';

export interface SrpViewProps {
  model: SrpViewModel;
}

const indexBaseMap: Record<string, string> = { en: 'en_search', fi: 'fi_hae', sv: 'sv_sok' };

export function SrpView({ model }: SrpViewProps): React.ReactElement {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { viewMode, setViewMode } = useViewMode({ initialLayout: model.viewMode });
  const { sort, setSort } = useSearchSort();
  const { query } = useSearchQuery();
  const { filters } = useSearchFilters();
  const { results } = useSearchResults();
  const isSearchLoading = useSearchLoading();
  const searchError = useSearchError();
  const { page } = useSearchPage();
  const [helpTrayOpen, setHelpTrayOpen] = useState(false);
  // Navbar live-index redesign (TEC navbar redesign Phase 2 item 2.2): the results count, sort
  // and posted-date selects, view toggle and feedback button all move into the header's own
  // filter tape (count, view toggle, "Clear all") and the Advanced Filters modal (sort, posted
  // date), see DesktopFilterCategories.tsx/TopFilters.tsx and AllFiltersModal.tsx. Flag off,
  // this row renders exactly as it did before this ticket.
  const liveIndex = useNavbarLiveIndexEnabled();
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  // Tray is not in the outer layout for the SRP island — render it here.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        e.key === '?' &&
        !helpTrayOpen &&
        window.innerWidth >= 768 &&
        !(document.activeElement instanceof HTMLInputElement) &&
        !(document.activeElement instanceof HTMLTextAreaElement) &&
        !(document.activeElement instanceof HTMLSelectElement)
      ) {
        e.preventDefault();
        setHelpTrayOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [helpTrayOpen]);

  const indexPrefix = indexBaseMap[model.locale] || 'en_search';
  const effectiveSort = sort || model.query.sort || 'relevance';
  const currentRefinement =
    effectiveSort === 'date'
      ? `${indexPrefix}_postedDate_desc`
      : effectiveSort === 'salary'
        ? `${indexPrefix}_salaryRangeMin_desc`
        : indexPrefix;

  // Review fix P4: the URL rewrite (adds `?sort=`, removes it for relevance, strips legacy
  // `[sort]` keys) now lives in the shared applySortPreference helper, also used by
  // AllFiltersModal's Sort section (see its handleSortSelect) so the two call sites can't drift
  // apart again. Only the value->preference parsing below is specific to this dropdown's option
  // values.
  const handleSortChange = (value: string) => {
    let sortPreference: 'relevance' | 'date' | 'salary' = 'relevance';
    if (value.includes('postedDate_desc')) sortPreference = 'date';
    else if (value.includes('salaryRangeMin_desc')) sortPreference = 'salary';
    applySortPreference(sortPreference, setSort);
  };

  const hasActiveSearch = Boolean(query) || (filters && Object.keys(filters).length > 0);

  // True for a real backend outage, not a genuine zero-hit search: either the live client search
  // just errored, or the frozen SSR model was itself built from the +data.ts error-fallback path
  // (see StaticSRPData.isBackendUnavailable) and no client fetch has replaced it yet. `results`
  // is seeded directly from `model.initialResults` (even in the error-fallback case that's a real
  // — if empty — object, never null), so reference equality is what actually detects "still
  // showing the frozen SSR snapshot" here; `!results` would never be true on this branch.
  const isBackendUnavailable = searchError || (model.isBackendUnavailable && results === model.initialResults);

  // model.hasNoResults is frozen at initial SSR/hydration; recompute from the live results so
  // client-side filter/query changes that return zero jobs actually show the empty state
  // instead of leaving the grid blank with no explanation. Skip while a search is in flight
  // to avoid a flash of "no results" between a filter change and the new results arriving.
  // A backend outage always counts as "no results" too, even with no active search (e.g. the
  // homepage default view failing to load) — otherwise the grid is left silently blank.
  const hasNoResults = useMemo(() => {
    if (isBackendUnavailable) return true;
    if (!hasActiveSearch || isSearchLoading) return false;
    if (!results) return model.hasNoResults;
    if (results.nbHits === 0) return true;
    if (Array.isArray(results.hits) && results.hits.length === 0) return true;
    return false;
  }, [hasActiveSearch, isSearchLoading, results, model.hasNoResults, isBackendUnavailable]);

  const filtersObj = filters && typeof filters === 'object' ? (filters as Record<string, string[]>) : {};
  const headerCompanyId =
    Array.isArray(filtersObj.companyId) && filtersObj.companyId.length > 0 ? filtersObj.companyId[0] : null;
  /**
   * Filters plus current URL path (not frozen SSR model).
   * Clearing the query can strip `/location/...` from the path while model.location would still show the old header.
   */
  const effectiveHeaderLocation = useMemo(() => {
    const livePathname =
      typeof window !== 'undefined' ? window.location.pathname : model.pathname;
    const matched = matchSearchIndexPath(livePathname);
    return getLocationFromFiltersAndRoute(filters, {
      routeParams: matched ? matched.routeParams : {},
    });
  }, [filters, model.pathname, query]);

  // Hydrated `model` is fixed after first paint; location trends must be refetched when filters/path change (client navigation).
  const ssrTrendsForHeaderLocation =
    effectiveHeaderLocation &&
    model.location &&
    effectiveHeaderLocation === model.location
      ? (model.locationTrendsData ?? null)
      : null;
  const { data: clientLocationTrendsData } = useLocationTrends(
    effectiveHeaderLocation,
    model.locale as 'en' | 'fi' | 'sv',
    ssrTrendsForHeaderLocation
  );
  const locationTrendsData = clientLocationTrendsData ?? ssrTrendsForHeaderLocation ?? undefined;
  const nbHits = results?.nbHits ?? model.pagination.nbHits;

  // In-feed (not above the header) — see spliceResultsInterstitial. Hidden on location pages,
  // in map view, and past page 1 (v1: only the first page of results carries the strip).
  const showTopEmployersStrip = !effectiveHeaderLocation && viewMode !== 'map' && (page ?? 0) === 0;
  const topEmployersInterstitial =
    showTopEmployersStrip && model.topEmployersStrip.length > 0 ? (
      <SrpTopEmployersStrip
        employers={model.topEmployersStrip}
        locale={model.locale}
        labels={model.staticStrings.bestCompaniesStrip}
      />
    ) : null;
  const topRolesInterstitial =
    showTopEmployersStrip && model.topRolesStrip.length > 0 ? (
      <SrpTopRolesStrip
        roles={model.topRolesStrip}
        locale={model.locale}
        labels={model.staticStrings.topRolesStrip}
      />
    ) : null;

  return (
    <>
      <KeyboardShortcutsTray
        isOpen={helpTrayOpen}
        onRequestClose={() => setHelpTrayOpen(false)}
        onCloseComplete={() => setHelpTrayOpen(false)}
      />
    <div className="w-full" data-srp-mode="interactive">
      <div className="max-w-screen-2xl mx-auto sm:px-0 md:px-2 lg:px-8">
        <div className={effectiveHeaderLocation && !model.roleHubData && !model.workModelHubData && !model.regionHubData ? 'lg:flex lg:flex-row lg:items-start lg:gap-6' : ''}>
        <SearchHeader
          location={effectiveHeaderLocation}
          knownLocationBackgrounds={model.knownLocationBackgrounds}
          locale={model.locale as 'en' | 'fi' | 'sv'}
          nbHits={nbHits}
          locationTrendsData={locationTrendsData}
          roleHubData={model.roleHubData}
          regionHubData={model.regionHubData}
          workModelHubData={model.workModelHubData}
          locationHubLinks={effectiveHeaderLocation === model.location ? model.locationHubLinks : []}
          companyHub={headerCompanyId && model.companyHub?.companyId === headerCompanyId ? model.companyHub : null}
        />
        {/* Below lg, map view drops this column's gap: SearchSidebar collapses to zero visible
            height there (its content moved to a floating overlay on the map itself — see
            MapView.tsx's `mapOverlay` prop below), so the gap was just adding a dead strip between
            the header and the map with nothing on either side of it to actually separate. lg+
            keeps it: desktop still shows SearchSidebar's toolbar row above the map. */}
        <div
          className={`ais-InstantSearch @container w-full min-w-0 flex flex-col lg:flex-1 ${
            viewMode === 'map' ? 'gap-0 lg:gap-2' : 'gap-2'
          }`}
        >
          {/* Phase 2 item 2.2 (and review fix P1): SearchSidebar always renders: it's where the
              SRP's <h1> lives for plain searches (see `hasPageLevelHeading` there: hub pages get
              their own h1 in SearchHeader, everything else gets it here in the stats row).
              Dropping the whole component when the flag is on used to drop that heading, an SEO
              regression. The `liveIndex` prop keeps the stats row (heading) exactly as before and
              hides ONLY the controls row: results count's siblings (view toggle, sort select,
              posted-date select, feedback button) move into the header's own tape/strip
              (DesktopFilterCategories.tsx via TopFilters.tsx/IndexStrip.tsx) and the Advanced
              Filters modal. The feedback entry point already exists in the hamburger menu
              (MobileMenuModal.tsx's "Give us feedback" row via FeedbackModalContext): verified,
              not duplicated here. The no-results / backend-unavailable messaging lives in
              SearchResults below, untouched either way. */}
          <SearchSidebar
            intl={intl}
            formatMessage={formatMessage}
            viewMode={viewMode}
            setViewMode={setViewMode}
            currentRefinement={currentRefinement}
            handleSortChange={handleSortChange}
            hasActiveSearch={hasActiveSearch}
            location={effectiveHeaderLocation}
            companyId={headerCompanyId}
            hasPageLevelHeading={Boolean(model.roleHubData || model.workModelHubData || model.regionHubData)}
            feedbackModalOpen={feedbackModalOpen}
            setFeedbackModalOpen={setFeedbackModalOpen}
            liveIndex={liveIndex}
          />
          {/* Map mode floats this over the map itself (see SearchResults' mapOverlay prop)
              instead of rendering it here, where it would push the map down — on an already
              vertical-space-constrained view (mobile especially), that could shove the map below
              the fold entirely. */}
          {!hasNoResults && viewMode !== 'map' && <SrpAlertCta hasActiveSearch={hasActiveSearch} />}
          <SearchResults
            viewMode={viewMode}
            setViewMode={setViewMode}
            hasNoResults={hasNoResults}
            isBackendUnavailable={isBackendUnavailable}
            query={model.query.query}
            interstitial={topEmployersInterstitial}
            secondInterstitial={topRolesInterstitial}
            mapOverlay={
              !hasNoResults && viewMode === 'map' ? (
                <div className="flex flex-col items-center gap-2">
                  {/* Only below lg: at lg+ SearchSidebar keeps this toggle in its normal
                      in-flow spot instead, since desktop has no vertical space pressure. */}
                  <MapViewModeToggle
                    viewMode={viewMode}
                    onSelectList={() => setViewMode('list')}
                    onSelectMap={() => setViewMode('map')}
                    formatMessage={formatMessage}
                    className="lg:hidden shadow-md"
                  />
                  <SrpAlertCta hasActiveSearch={hasActiveSearch} />
                </div>
              ) : null
            }
          />
        </div>
        </div>
        {viewMode !== 'map' && !hasNoResults && (
          <SearchPagination
            viewMode={viewMode}
            helpTrayOpen={helpTrayOpen}
            setHelpTrayOpen={setHelpTrayOpen}
          />
        )}
        <SrpActivitySankeyStripLive
          viewMode={viewMode}
          locale={model.locale}
          strings={model.staticStrings.activityStrip}
        />
      </div>
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        source="search_results"
        metadata={{ page: model.pathname }}
      />
      {/* Map view fills the viewport on its own (see MapView's height calc) — a footer below it
          would make the page taller than the screen, turning the whole page into a scroll
          container the user can accidentally drag instead of the map. */}
      {viewMode !== 'map' && <Footer locale={model.locale} strings={model.footerStrings} />}
    </div>
    </>
  );
}

